import {
  GUEST_DAILY_FREE_INSPECTS,
  GUEST_DAILY_FREE_ZOOM_OUTS,
} from '../../constants/games/unlimitedGame';

export const UNLIMITED_GAME_STORAGE_KEY = 'zoomedInUnlimitedGame';
export const FREE_SKIPS_STORAGE_KEY = 'zoomedInFreeSkips';
export const FREE_INSPECTS_STORAGE_KEY = 'zoomedInFreeInspects';
export const USED_WORD_IMAGES_STORAGE_KEY = 'usedWordImages';
export const GUEST_DAILY_PROGRESS_STORAGE_KEY = 'zoomedInUnlimitedGuestDailyProgress';
export const GUEST_DAILY_POWERUPS_STORAGE_KEY = 'zoomedInUnlimitedGuestDailyPowerups';

const getTodayUtcDate = () => new Date().toISOString().slice(0, 10);

const readGuestDailyProgress = () => {
  try {
    const raw = localStorage.getItem(GUEST_DAILY_PROGRESS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const date = typeof parsed.date === 'string' ? parsed.date : '';
    const count = Number.isFinite(parsed.count)
      ? Math.max(0, Math.floor(parsed.count))
      : 0;
    if (!date) return null;
    return { date, count };
  } catch {
    return null;
  }
};

const writeGuestDailyProgress = ({ date, count }) => {
  try {
    localStorage.setItem(
      GUEST_DAILY_PROGRESS_STORAGE_KEY,
      JSON.stringify({
        date,
        count: Math.max(0, Math.floor(count)),
      })
    );
  } catch (error) {
    console.error('Error writing guest daily game progress:', error);
  }
};

const clampToNonNegativeInt = (value) =>
  Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;

const readGuestDailyPowerupUsage = () => {
  try {
    const raw = localStorage.getItem(GUEST_DAILY_POWERUPS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const date = typeof parsed.date === 'string' ? parsed.date : '';
    if (!date) return null;
    return {
      date,
      inspectUsed: clampToNonNegativeInt(parsed.inspectUsed),
      zoomOutUsed: clampToNonNegativeInt(parsed.zoomOutUsed),
    };
  } catch {
    return null;
  }
};

const writeGuestDailyPowerupUsage = ({ date, inspectUsed, zoomOutUsed }) => {
  try {
    localStorage.setItem(
      GUEST_DAILY_POWERUPS_STORAGE_KEY,
      JSON.stringify({
        date,
        inspectUsed: clampToNonNegativeInt(inspectUsed),
        zoomOutUsed: clampToNonNegativeInt(zoomOutUsed),
      })
    );
  } catch (error) {
    console.error('Error writing guest daily powerup usage:', error);
  }
};

const getCurrentGuestDailyPowerupUsage = () => {
  const today = getTodayUtcDate();
  const usage = readGuestDailyPowerupUsage();
  if (!usage || usage.date !== today) {
    const resetUsage = { date: today, inspectUsed: 0, zoomOutUsed: 0 };
    writeGuestDailyPowerupUsage(resetUsage);
    return resetUsage;
  }
  return usage;
};

const mapUsageToRemaining = ({ inspectUsed, zoomOutUsed }) => ({
  inspectRemaining: Math.max(0, GUEST_DAILY_FREE_INSPECTS - inspectUsed),
  zoomOutRemaining: Math.max(0, GUEST_DAILY_FREE_ZOOM_OUTS - zoomOutUsed),
});

export const manageUsedWordImages = {
  get: () => {
    try {
      return new Set(
        JSON.parse(localStorage.getItem(USED_WORD_IMAGES_STORAGE_KEY) || '[]')
      );
    } catch {
      return new Set();
    }
  },
  add: (word, imageId, source) => {
    try {
      const key = `${word}-${source}-${imageId}`;
      const usedWordImages = new Set(
        JSON.parse(localStorage.getItem(USED_WORD_IMAGES_STORAGE_KEY) || '[]')
      );
      usedWordImages.add(key);
      let wordImagesArray = Array.from(usedWordImages);
      if (wordImagesArray.length > 500) {
        wordImagesArray = wordImagesArray.slice(-500);
      }
      localStorage.setItem(
        USED_WORD_IMAGES_STORAGE_KEY,
        JSON.stringify(wordImagesArray)
      );
    } catch (error) {
      console.error('Error managing used word images:', error);
    }
  },
  reset: () => {
    localStorage.removeItem(USED_WORD_IMAGES_STORAGE_KEY);
  },
};

export const getSavedGame = () => {
  try {
    return JSON.parse(localStorage.getItem(UNLIMITED_GAME_STORAGE_KEY) || 'null');
  } catch (error) {
    console.error('Error reading saved game:', error);
    return null;
  }
};

export const saveGame = (gameState, usedLetters) => {
  const stateToSave = {
    ...gameState,
    usedLetters: Array.from(usedLetters),
  };
  localStorage.setItem(UNLIMITED_GAME_STORAGE_KEY, JSON.stringify(stateToSave));
};

export const clearSavedGame = () => {
  localStorage.removeItem(UNLIMITED_GAME_STORAGE_KEY);
};

export const getGuestDailyGameCount = () => {
  const today = getTodayUtcDate();
  const progress = readGuestDailyProgress();
  if (!progress || progress.date !== today) return 0;
  return progress.count;
};

export const incrementGuestDailyGameCount = () => {
  const today = getTodayUtcDate();
  const nextCount = getGuestDailyGameCount() + 1;
  writeGuestDailyProgress({ date: today, count: nextCount });
  return nextCount;
};

export const getGuestDailyPowerupsRemaining = () => {
  const usage = getCurrentGuestDailyPowerupUsage();
  return mapUsageToRemaining(usage);
};

export const consumeGuestDailyPowerup = (type) => {
  const usage = getCurrentGuestDailyPowerupUsage();
  let nextUsage = usage;

  if (type === 'inspect' && usage.inspectUsed < GUEST_DAILY_FREE_INSPECTS) {
    nextUsage = { ...usage, inspectUsed: usage.inspectUsed + 1 };
    writeGuestDailyPowerupUsage(nextUsage);
  }

  if (type === 'zoom_out' && usage.zoomOutUsed < GUEST_DAILY_FREE_ZOOM_OUTS) {
    nextUsage = { ...usage, zoomOutUsed: usage.zoomOutUsed + 1 };
    writeGuestDailyPowerupUsage(nextUsage);
  }

  return mapUsageToRemaining(nextUsage);
};
